import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/authStore'

import authRoutes from './authRoutes'
import adminRoutes from './adminRoutes'
import websiteRoutes from './websiteRoutes'
import gameRoutes from './gameRoutes'
import errorRoutes from './errorRoutes'

// Add error handling for dynamic imports
const handleModuleImportError = (error: unknown) => {
  console.error('Failed to load module:', error)
  return import('../views/ErrorPages/404.not-found.vue')
}

const processRoutes = (routes: RouteRecordRaw[]): RouteRecordRaw[] => {
  return routes.map((route) => {
    if (typeof route.component === 'function') {
      const originalComponent = route.component as () => Promise<unknown>
      route.component = () => originalComponent().catch(handleModuleImportError)
    }

    if ('children' in route && route.children) {
      route.children = processRoutes(route.children)
    }

    return route
  })
}

const routes = processRoutes([
  ...authRoutes,
  ...adminRoutes,
  ...websiteRoutes,
  ...gameRoutes,
  ...errorRoutes
])

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  linkActiveClass: 'active',
  linkExactActiveClass: 'active',
  scrollBehavior(to, _from, _savedPosition) {
    if (to.hash) {
      return {
        el: to.hash,
        top: 95,
        behavior: 'smooth'
      }
    }
  },
  routes: routes
})

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()

  await authStore.getSignedInUser(authStore)

  if (!authStore.firebaseUser) {
    if (to.matched.some((record) => record.meta.requiresAuth)) {
      next({ name: 'sign-in' })
      return
    }
    next()
    return
  }

  const idTokenResult = await authStore.firebaseUser.getIdTokenResult()

  if (
    ['auth', 'sign-in', 'sign-up', 'forgot-password'].includes(to.name as string) &&
    authStore.firebaseUser
  ) {
    next({ name: 'home' })
    return
  }

  if (to.matched.some((record) => record.meta.requiresAdmin) && authStore.firebaseUser) {
    if (!idTokenResult.claims.admin) {
      next({ name: 'forbidden' })
      return
    }
  }

  if (to.name === 'admin' && authStore.firebaseUser) {
    next('/admin/packs')
    return
  }

  if (to.matched.some((record) => record.meta.requiresAuth) && !authStore.firebaseUser) {
    next({ name: 'sign-in' })
    return
  }

  if (to.matched.some((record) => record.meta.requiresAcceptance) && authStore.firebaseUser) {
    const userInfo = authStore.dbUser

    if (!userInfo) {
      next({ name: 'home' })
      return
    }

    if (!userInfo?.disclaimerAccepted) {
      next({ name: 'disclaimer-game' })
      return
    }
  }

  next()
})

export default router
